import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { AppProps } from 'next/app'
import { wrapper } from '../redux/store'
import { ThemeProvider } from '@material-ui/core'
import 'react-datepicker/dist/react-datepicker.css'
import 'swiper/swiper-bundle.min.css'
import 'glider-js/glider.min.css'
import useGlobalStyles from '../components/Global/Global.style'
import { SnackbarProvider } from 'notistack'
import InformationChip from '../components/InformationChip/InformationChip'
import { PageTransition } from 'next-page-transitions'
import Loader from '../components/Loader/Loader'
import dayjs from 'dayjs'
import { i18nextInit } from '../i18n.js'
import { useRouter } from 'next/router'
import { IKContext } from 'imagekitio-react'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { DATADOG_KEY } from '../utils/constants'
import * as gtag from '../utils/gtag'

import { TRANSITION_TIMEOUT } from '../utils/utils'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import { GAEvent, GTMEvent } from '../typings/base'
import { PrismicPreview } from '@prismicio/next'
import useConsent from '../utils/useConsent'
import {
  ConsentState,
  consentManager,
  hasAnyConsent,
  isWithdrawal,
  NO_CONSENT,
} from '../utils/consent'
import { createInstance } from '@datapunt/matomo-tracker-react'
import { GoogleAnalytics } from '@next/third-parties/google'

const Theme = require(`../stores/${process.env.STORE}/theme.ts`)
const customParseFormat = require('dayjs/plugin/customParseFormat')
dayjs.extend(customParseFormat)
import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { PreviousUrlProvider } from '@/components/PreviousUrlProvider'
import { safeDataLayerPush } from '@/utils/gtag'
import { GTMScript } from '@/components/GTMScript'
import { HotjarScript } from '../components/HotjarScript'

const InStoreManager = dynamic(() => import('../components/InStoreManager'))
const CssBaseline = dynamic(() => import('@material-ui/core/CssBaseline'))
const SSO = dynamic(() => import('../components/SSOManager'))
const CartManager = dynamic(() => import('../components/CartManager'))
const VersionManager = dynamic(() => import('../components/VersionManager'))
const Auth = dynamic(() => import('../components/AuthManager'))
const MatomoProvider = dynamic(() =>
  import('@datapunt/matomo-tracker-react').then((mod) => mod.MatomoProvider)
)
const Provider = dynamic(() =>
  import('next-auth/client').then((mod) => mod.Provider)
)
declare const window: any

const startDataDogMonitoring = function () {
  if (window.DD_STARTED) return
  datadogRum.init({
    applicationId: 'fef1c5dc-fc2f-4990-bb0e-63e42d993679',
    clientToken: DATADOG_KEY,
    site: 'datadoghq.eu',
    proxy: 'https://log-collector.lizee.io',
    service: 'rp-' + process.env.STORE,
    env: process.env.ENV,
    allowedTracingUrls: [
      function (url) {
        return (
          !url.includes('keycloak.') &&
          (url.includes('.lizee.io') || url.includes('http://localhost'))
        )
      },
    ],
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 30,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  })
  datadogLogs.init({
    clientToken: DATADOG_KEY,
    site: 'datadoghq.eu',
    proxy: 'https://log-collector.lizee.io',
    forwardErrorsToLogs: true,
    sessionSampleRate: 30,
    env: process.env.ENV,
    service: 'rp-' + process.env.STORE,
  })
  window.DD_STARTED = true
}
const WlApp = ({ Component, pageProps, router }: AppProps) => {
  const nextRouter = useRouter()
  i18nextInit(nextRouter, pageProps.i18nResources)
  const isDisplayedBannerOnAllPages =
    pageProps?.banner?.data?.is_displayed_banner_on_all_pages
  const isHomePage = nextRouter.route === '/'
  const LayoutStyle = {
    isDisplayedBannerOnAllPages,
    isHomePage,
  }

  useGlobalStyles(LayoutStyle)

  const [matomoInstance, setMatomoInstance] = useState(null)
  const [shouldAddGAScript, setShouldAddGAScript] = useState(false)
  const [shouldAddGTMScript, setShouldAddGTMScript] = useState(false)
  const [shouldAddMatomoScript, setShouldAddMatomoScript] = useState(false)
  const [shouldAddHotjarScript, setShouldAddHotjarScript] = useState(false)
  const [shouldAddMetaPixel, setShouldAddMetaPixel] = useState(false)

  useEffect(() => {
    const handleRouteChange = (url) => {
      gtag.tracker(GAEvent.PAGE_VIEW, {
        page_location: url,
      })
      safeDataLayerPush({ event: GTMEvent.PAGE_VIEW, page_location: url })
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'PageView')
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  // What the page already runs. Starts empty: nothing loads before a choice.
  const appliedConsent = useRef<ConsentState>(NO_CONSENT)

  const applyConsent = useCallback((consent: ConsentState) => {
    const before = appliedConsent.current

    // A loaded script cannot be unloaded: a withdrawn purpose needs a fresh
    // page, which starts again from the stored choice. Lizee Consent does it
    // itself, after deleting the withdrawn purposes' cookies.
    if (isWithdrawal(before, consent)) {
      if (consentManager() === 'cookiebot') window.location.reload()
      return
    }
    appliedConsent.current = consent

    const _mtm = (window._mtm = window._mtm || [])
    if (hasAnyConsent(consent) && !hasAnyConsent(before)) {
      _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' })
      const matomoTracker = createInstance({
        urlBase: process.env.MATOMO_URL
          ? process.env.MATOMO_URL
          : 'https://analytics.lizee.io/',
        siteId: process.env.MATOMO_ID ? +process.env.MATOMO_ID : 4,
      })
      setMatomoInstance(matomoTracker)
      setShouldAddMatomoScript(true)
    }

    if (consent.statistics && !before.statistics) {
      _mtm.push({ event: 'cookie_consent_statistics' })
      setShouldAddGAScript(true)
      // Without a consent manager, GTM comes from the snippet in _document.
      if (consentManager() !== 'none') setShouldAddGTMScript(true)
      setShouldAddHotjarScript(true)
      // Datadog starts on the first interaction only, to save money.
      window.addEventListener('mousemove', startDataDogMonitoring)
      window.addEventListener('mousewheel', startDataDogMonitoring)
    }

    if (consent.preferences && !before.preferences) {
      _mtm.push({ event: 'cookie_consent_preferences' })
    }

    if (consent.marketing && !before.marketing) {
      _mtm.push({ event: 'cookie_consent_marketing' })
      setShouldAddMetaPixel(true)
    }
  }, [])

  useConsent(applyConsent)

  useEffect(() => {
    // Global redirect check
    if (process.env.REDIRECT === 'true') {
      const currentUrl = window.location.href
      const maintenanceUrl =
        'https://secondemain.maje.com/pages/de-retour-bientot'

      // Avoid infinite redirect loop - don't redirect if already on maintenance page
      if (currentUrl !== maintenanceUrl) {
        window.location.href = maintenanceUrl
        return
      }
    }

    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side')

    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles)
    }
  }, [])

  const googleAnalyticsId = process.env.NEXT_PUBLIC_GA_ID
  const metaPixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID
  const brevoFormId = process.env.NEXT_PUBLIC_BREVO_FORM_ID
  const prismicURL = process.env.PRISMIC_URL
  const prismicRepo = prismicURL
    ? prismicURL.split('https://')[1].split('.cdn.prismic.io/api/v2')[0]
    : ''
  const enableGTMWithoutConsent =
    process.env.NEXT_PUBLIC_ENABLE_GTM_WITHOUT_CONSENT

  const initialPaypalOptions = {
    currency: process.env.PAYPAL_CURRENCY ? process.env.PAYPAL_CURRENCY : 'EUR',
    clientId: process.env.PAYPAL_CLIENT_ID,
  }

  const renderAppContent = () => (
    <PreviousUrlProvider>
      <MatomoProvider value={matomoInstance}>
        <ThemeProvider theme={Theme.default}>
          <CssBaseline />
          <PageTransition
            key={router.asPath}
            timeout={TRANSITION_TIMEOUT}
            classNames="page-transition"
            loadingComponent={<Loader />}
            loadingDelay={500}
            loadingTimeout={{
              enter: TRANSITION_TIMEOUT,
              exit: 0,
            }}
            loadingClassNames="loading-indicator"
          >
            <SnackbarProvider
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              content={(key, message) => (
                <InformationChip
                  key={key}
                  message={message}
                  i18n={pageProps.i18nResources.translations}
                />
              )}
              autoHideDuration={3000}
            >
              {process.env.PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider options={initialPaypalOptions}>
                  <App />
                </PayPalScriptProvider>
              ) : (
                <App />
              )}
            </SnackbarProvider>
          </PageTransition>
        </ThemeProvider>
      </MatomoProvider>
    </PreviousUrlProvider>
  )

  const App = () => (
    <Auth>
      <CartManager>
        <InStoreManager>
          <SSO>
            <VersionManager>
              <IKContext
                urlEndpoint={process.env.IMGKIT_URL} // Required. Default URL-endpoint is https://ik.imagekit.io/your_imagekit_id
                publicKey={process.env.IMGKIT_PUBLIC_KEY} // optional
                transformationPosition="path" // optional
              >
                <Component {...pageProps} />
              </IKContext>
            </VersionManager>
          </SSO>
        </InStoreManager>
      </CartManager>
    </Auth>
  )

  return (
    <React.Fragment>
      {(Boolean(enableGTMWithoutConsent) || shouldAddGTMScript) && (
        <GTMScript />
      )}
      {shouldAddHotjarScript && <HotjarScript />}
      {shouldAddMatomoScript && (
        <Script
          async
          type="text/javascript"
          src={`https://analytics.lizee.io/js/container_${process.env.MTM_ID}.js`}
        />
      )}
      {shouldAddGAScript && googleAnalyticsId && (
        <GoogleAnalytics gaId={googleAnalyticsId} />
      )}

      {shouldAddMetaPixel && metaPixelId && (
        <>
          <Script
            id="fb-pixel"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                !function(f,b,e,v,n,t,s){
                  if(f.fbq)return; n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0';
                  n.queue=[]; t=b.createElement(e); t.async=!0;
                  t.src=v; s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)
                }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${metaPixelId}');
                fbq('track', 'PageView');
              `,
            }}
          />
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src={`https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1`}
            />
          </noscript>
        </>
      )}

      {brevoFormId && (
        <>
          <Script
            src="https://cdn.brevo.com/js/sdk-loader.js"
            strategy="afterInteractive"
            async
          />
          <Script
            id="brevo-popup"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
          window.Brevo = window.Brevo || [];
          Brevo.push([
            "init",
            {
              client_key: "${process.env.NEXT_PUBLIC_BREVO_FORM_ID}"
            }
          ]);
        `,
            }}
          />
        </>
      )}
      {nextRouter.isPreview ? (
        <PrismicPreview repositoryName={prismicRepo}>
          <Provider session={pageProps.session}>{renderAppContent()}</Provider>
        </PrismicPreview>
      ) : (
        <Provider session={pageProps.session}>{renderAppContent()}</Provider>
      )}
    </React.Fragment>
  )
}

export default wrapper.withRedux(WlApp)
